import React, { useState, useEffect } from 'react';
import { Grid } from '@mui/material';
import carViewsImage from './assets/carviews.png';
import { Alert } from '@mui/material';
import SignatureCapture from './components/SignatureCanvas';
import { v4 as uuidv4 } from 'uuid';
import {
    CenteredContainer, GalleryButtonContainer, CenteredTypography,
    TitleTypography, InputStyled, TermsAndConditionsLink, CheckboxContainer,
    CheckboxStyled, CameraButtonContainer, PhotoLibraryIconStyled,
    DeleteButtonStyled, ButtonStyled, CarViewsImage
} from './StyledComponents';

const getViewImage = (index) => {
    const viewNumber = index <= 10 ? index + 1 : 11; // Si el índice es 10 o mayor, usa view10
    return require(`./assets/icons/view${viewNumber}.png`);
};

const ContentComponent = ({ isPickup }) => {

    const backendLink = 'https://carwas-pd0c.onrender.com'; //https://carwas-pd0c.onrender.com o http://localhost:7000
    const [fullName, setFullName] = useState(localStorage.getItem('fullName') || '');
    const [email, setEmail] = useState(localStorage.getItem('email') || '');
    const [images, setImages] = useState(new Array(12).fill(null));
    const [mediaStream, setMediaStream] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [signatureImage, setSignatureImage] = useState('');
    const [acceptedTerms, setAcceptedTerms] = useState(false);

    useEffect(() => {
        localStorage.setItem('fullName', fullName);
        localStorage.setItem('email', email);
    }, [fullName, email]);

    const userId = localStorage.getItem('userId');
    if (!userId) {
        const newUserId = uuidv4();
        localStorage.setItem('userId', newUserId);
    }

    useEffect(() => {
        const handleBeforeUnload = async (e) => {
            await fetch(`${backendLink}/delete-images`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userId,
                }),
            });
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [userId]);

    const handleSignatureSave = async (imageData) => {
        if (imageData) {
            setSignatureImage(imageData);

            try {
                const formData = new FormData();
                formData.append('image', dataURItoBlob(imageData));
                formData.append('buttonIndex', 'signature');
                formData.append('imageName', `${userId}_signature.jpg`);

                const response = await fetch(`${backendLink}/upload-image`, {
                    method: 'POST',
                    body: formData,
                });

                if (response.ok) {
                    console.log('Signature image uploaded successfully');
                } else {
                    console.error('Signature image upload failed');
                }
            } catch (error) {
                console.error('Error uploading signature image:', error);
            }
        }
    };

    const dataURItoBlob = (dataURI) => {
        if (!dataURI) return null;

        const byteString = atob(dataURI.split(',')[1]);
        const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        return new Blob([ab], { type: mimeString });
    };


    const handleImageSelect = async (index, event) => {
        try {
            const file = event.target.files[0];

            if (!file) {
                return;
            }

            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const buttonIndex = index + 1;
            const imageName = `${userId}_${year}_${month}_${day}_${buttonIndex}.jpg`;
            const imageBlob = await resizeAndConvertToBlob(file, 800, 800, 0.8);

            setImages((prevImages) => {
                const updatedImages = prevImages.map((img, i) =>
                    i === index ? URL.createObjectURL(imageBlob) : img
                );

                if (updatedImages.filter(img => img !== null).length === updatedImages.length && updatedImages.length < 16) {
                    return [...updatedImages, null];
                }

                return updatedImages;
            });

            const formData = new FormData();
            formData.append('image', imageBlob);
            formData.append('buttonIndex', buttonIndex);
            formData.append('imageName', imageName);

            const response = await fetch(`${backendLink}/upload-image`, {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                console.log('Image uploaded successfully');
            } else {
                console.error('Image upload failed');
            }
        } catch (error) {
            console.error('Error selecting image:', error);
        }
    };

    const resizeAndConvertToBlob = async (file, maxWidth, maxHeight, quality) => {
        return new Promise((resolve) => {
            const reader = new FileReader();

            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;

                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    if (width > maxWidth) {
                        height *= maxWidth / width;
                        width = maxWidth;
                    }

                    if (height > maxHeight) {
                        width *= maxHeight / height;
                        height = maxHeight;
                    }

                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    canvas.toBlob((blob) => {
                        resolve(blob);
                    }, 'image/jpeg', quality);
                };
            };

            reader.readAsDataURL(file);
        });
    };

    const handleDeleteImage = async (index) => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const buttonIndex = index + 1;
        const imageName = `${userId}_${year}_${month}_${day}_${buttonIndex}.jpg`;

        setImages((prevImages) =>
            prevImages.map((img, i) => (i === index ? null : img))
        );

        await deleteImageFromServer(imageName);
    };

    const deleteImageFromServer = async (imageFullName) => {
        try {
            const response = await fetch(`${backendLink}/delete-image`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userId: userId,
                    imageSuffix: imageFullName,
                }),
            });

            if (!response.ok) {
                throw new Error('Error deleting image from server.');
            }

            console.log('Image deleted successfully');
        } catch (error) {
            console.error('Error deleting image:', error);
        }
    };

    const handleClearSignature = async () => {
        setSignatureImage('');

        await deleteImageFromServer(`${userId}_signature.jpg`);
    };

    const handleSendInformation = async () => {
        try {
            const invalidNameCharsRegex = /[\d@*()!]/;

            if (!fullName || invalidNameCharsRegex.test(fullName) || !email || !email.includes('@') || !email.includes('.')) {
                setErrorMessage('Please provide valid full name and email');

                setTimeout(() => {
                    setErrorMessage('');
                }, 3000);

                return;
            }

            if (images.filter(img => img !== null).length < 1) {
                setErrorMessage('Please upload at least one image.');
                return;
            }
            if (!signatureImage) {
                alert('Please, enter your signature.');
                return;
            }

            if (!acceptedTerms) {
                setErrorMessage('Please, accept terms and conditions.');
                return;
            }

            setErrorMessage('');
            setSending(true);

            let latitude = "N/A";
            let longitude = "N/A";
            let city = "N/A";
            let region = "N/A";

            async function getLocationFromIpapi() {
                try {
                    const response = await fetch('https://ipapi.co/json/');
                    if (!response.ok) throw new Error('No fue posible obtener la respuesta de ipapi.');
                    const data = await response.json();
                    if (!data.city || !data.region) throw new Error('Datos incompletos de ipapi.');
                    return data;
                } catch (error) {
                    console.error("Error with ipapi:", error);
                    return null;
                }
            }

            async function getLocationFromIpinfo() {
                try {
                    const response = await fetch('https://ipinfo.io/json');
                    if (!response.ok) throw new Error('No fue posible obtener la respuesta de ipinfo.');
                    const data = await response.json();
                    if (!data.loc) throw new Error('Datos de ubicación no disponibles en ipinfo.');
                    const [lat, lon] = data.loc.split(',');
                    return {
                        city: data.city,
                        region: data.region,
                        latitude: lat,
                        longitude: lon
                    };
                } catch (error) {
                    console.error("Error with ipinfo:", error);
                    return null;
                }
            }

            async function getLocation() {
                let locationData = await getLocationFromIpapi();
                if (!locationData) {
                    locationData = await getLocationFromIpinfo();
                }
                if (locationData) {
                    city = locationData.city || city;
                    region = locationData.region || region;
                    latitude = locationData.latitude || latitude;
                    longitude = locationData.longitude || longitude;
                }
            }

            await getLocation();

            const response = await fetch(`${backendLink}/send-email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    fullName,
                    email,
                    latitude,
                    longitude,
                    city,
                    region,
                    userId,
                    isPickup,
                }),
            });

            if (response.ok) {
                console.log('Information sent successfully');
                alert('Information sent successfully, you can now continue to your car!');
                const deleteResponse = await fetch(`${backendLink}/delete-images`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        userId,
                    }),
                });

                if (deleteResponse.ok) {
                    console.log('Images deleted successfully');
                } else {
                    console.error('Failed to delete images');
                }

                window.location.reload();
            } else {
                const errorMessage = await response.text();
                console.error('Failed to send information:', errorMessage);
                alert('Failed to send information. Please, try again or contact a member of the company.');
            }

            setSending(false);
        } catch (error) {
            console.error('Error sending information:', error);
            setSending(false);
            alert('Failed to send information. Please, try again or contact a member of the company.');
        }
    };

    return (
        <CenteredContainer><br /><br />
            <TitleTypography variant="h4">Car Walk Around</TitleTypography><br />
            <TitleTypography variant="h1">{isPickup ? 'Pickup Process' : 'Return Process'}</TitleTypography>
            <br />
            <CenteredTypography variant="body1" gutterBottom>
                <b>
                    {isPickup
                        ? 'To ensure your review process and guarantee the condition of the vehicle before use, be sure to provide your real information!'
                        : 'To ensure your return process and guarantee the condition of the vehicle after use, be sure the same information from the pickup!'}
                </b>
            </CenteredTypography><br />
            <InputStyled
                label="Full name"
                variant="outlined"
                fullWidth
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
            />
            <InputStyled
                label="Email address"
                variant="outlined"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
            />
            <CenteredTypography variant="body1" gutterBottom>
                You're responsible to report any vehicle damage before leaving at pickup and when returning it. Unreported damages imply the vehicle's condition matches prior documentation.
            </CenteredTypography>
            <CarViewsImage src={carViewsImage} alt="Car Views" />
            <CenteredTypography variant="body1" gutterBottom>
                <span style={{ fontWeight: 'bold', color: '#dbc363' }}>Touch the icons </span>
                to upload each image. Your camera will automatically open. Take a picture of the corresponding part of the car.
            </CenteredTypography>
            <CameraButtonContainer container spacing={0}>
                <GalleryButtonContainer container spacing={3}>
                    {images.map((image, index) => (
                        <Grid item xs={3} key={index} style={{ position: 'relative' }}>
                            {image && (
                                <DeleteButtonStyled onClick={() => handleDeleteImage(index)} />
                            )}
                            {image ? (
                                <img
                                    src={image}
                                    alt={`Image ${index + 1}`}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            ) : (
                                <label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        style={{ display: 'none' }}
                                        onChange={(event) => handleImageSelect(index, event)}
                                    />
                                    <img
                                        src={getViewImage(index)}
                                        alt="Upload"
                                        style={{ width: '72px', height: '72px', cursor: 'pointer' }}
                                    />
                                </label>
                            )}
                        </Grid>
                    ))}
                </GalleryButtonContainer>
            </CameraButtonContainer><br /><br />
            <CenteredTypography variant="body1" gutterBottom>
                <b>Please enter your signature on the following canvas. It will be automatically save.</b>
            </CenteredTypography>
            <SignatureCapture onSave={handleSignatureSave} onClear={handleClearSignature} />
            <CheckboxContainer>
                <CheckboxStyled
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                />
                <span>
                    Accept
                    <TermsAndConditionsLink
                        href="https://drive.google.com/file/d/1qUzcF3mbIv1VS_lODQLAFWSKohfGUXw0/view"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Terms and Conditions
                    </TermsAndConditionsLink>
                </span>
            </CheckboxContainer>
            <ButtonStyled
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleSendInformation}
                disabled={sending}
            >
                {sending ? 'Sending, please wait' : 'Send information'}
            </ButtonStyled><br />
            <TermsAndConditionsLink variant="body2" href="tel:+12693045377" target="_blank">
                For support please call +1 (269) 304-5377
            </TermsAndConditionsLink>
            {' '}
            or send an email to{' '}
            <TermsAndConditionsLink variant="body2" href="mailto:info@rac4less.com">
                info@rac4less.com
            </TermsAndConditionsLink>
            {errorMessage && (
                <Alert severity="error" style={{ marginTop: '16px' }}>
                    {errorMessage}
                </Alert>
            )}
            <br /><br /><br />
        </CenteredContainer>
    );
};

export default ContentComponent;